// src/components/DashboardSidebar.tsx
import React from 'react';
import { Layout, Menu } from 'antd';
import {
  UserOutlined,
  LogoutOutlined,
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  SettingOutlined,
  MedicineBoxOutlined,
  InboxOutlined,
  ExperimentOutlined,
  DollarCircleOutlined,
  TeamOutlined,
  GiftOutlined,
  UnorderedListOutlined,
  ClockCircleOutlined,
  LoginOutlined,
  CalendarOutlined,
  SafetyCertificateOutlined,
  FileTextOutlined,
  PlusCircleOutlined,
  FundOutlined,
  FileExcelOutlined,
} from '@ant-design/icons';
import { useLanguage } from './LanguageContext';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useOwnerContext } from './OwnerContext';

const { Sider } = Layout;

interface SidebarProps {
  collapsed: boolean;
  setCollapsed: (value: boolean) => void;
}

const DashboardSidebar: React.FC<SidebarProps> = ({ collapsed, setCollapsed }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { username, logout } = useAuth();
  const { t } = useLanguage();
  const { canSeeRevenueAnalytics, canManageOwnerFeatures, canAccessInsurance, isViewerOnly } = useOwnerContext();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleMenuClick = (key: string) => {
    switch (key) {
      case 'patients':
        navigate('/dashboard');
        break;
      case 'services':
        navigate('/services');
        break;
      case 'analytics':
        navigate('/analytics');
        break;
      case 'inventory':
        navigate('/inventory');
        break;
      case 'external-services':
        navigate('/external-services');
        break;
      case 'visit-types':
        navigate('/visit-types');
        break;
      case 'referral-sources':
        navigate('/referral-sources');
        break;
      case 'packages':
        navigate('/packages');
        break;
      case 'medicines':
        navigate('/medicines');
        break;
      case 'attendance':
        navigate('/attendance');
        break;
      case 'doctor-attendance':
        navigate('/doctor-attendance');
        break;
      case 'appointments':
        navigate('/appointments');
        break;
      case 'timetable':
        navigate('/timetable');
        break;
      case 'expenses':
        navigate('/expenses');
        break;
      case 'insurance-companies':
        navigate('/insurance-companies');
        break;
      case 'insurance-report':
        navigate('/insurance-report');
        break;
      case 'owner-doctors':
        navigate('/owner/doctors');
        break;
      case 'owner-revenue':
        navigate('/owner/revenue');
        break;
      case 'patient-export':
        navigate('/owner/patient-export');
        break;
      case 'owner-visit-reports':
        navigate('/owner/visit-reports');
        break;
      case 'settings':
        navigate('/settings');
        break;
    }
  };

  // Determine current selected key based on path
  const getSelectedKey = () => {
    const path = location.pathname;
    if (path.includes('/services')) return 'services';
    if (path.includes('/analytics')) return 'analytics';
    if (path.includes('/inventory')) return 'inventory';
    if (path.includes('/external-services')) return 'external-services';
    if (path.includes('/visit-types')) return 'visit-types';
    if (path.includes('/referral-sources')) return 'referral-sources';
    if (path.includes('/packages')) return 'packages';
    if (path.includes('/medicines')) return 'medicines';
    if (path.includes('/expenses')) return 'expenses';
    if (path.includes('/doctor-attendance')) return 'doctor-attendance';
    if (path.includes('/attendance')) return 'attendance';
    if (path.includes('/appointments')) return 'appointments';
    if (path.includes('/timetable')) return 'timetable';
    if (path.includes('/insurance-report')) return 'insurance-report';
    if (path.includes('/insurance-companies')) return 'insurance-companies';
    if (path.includes('/owner/doctors')) return 'owner-doctors';
    if (path.includes('/owner/revenue')) return 'owner-revenue';
    if (path.includes('/owner/patient-export')) return 'patient-export';
    if (path.includes('/owner/visit-reports')) return 'owner-visit-reports';
    if (path.includes('/settings')) return 'settings';
    return 'patients';
  };

  return (
    <Sider
      collapsible
      collapsed={collapsed}
      onCollapse={(value) => setCollapsed(value)}
      trigger={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
    >
      <div
        className="logo"
        style={{
          height: '32px',
          background: 'rgba(255, 255, 255, 0.2)',
          margin: '16px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          paddingLeft: collapsed ? 0 : '10px',
        }}
      >
        <UserOutlined style={{ color: 'white', marginRight: collapsed ? 0 : '10px' }} />
        {!collapsed && (
          <span style={{ color: 'white', whiteSpace: 'nowrap', overflow: 'hidden' }}>
            {username || 'User'}
          </span>
        )}
      </div>
      <Menu
        theme="dark"
        mode="inline"
        selectedKeys={[getSelectedKey()]}
        defaultOpenKeys={location.pathname.includes('/insurance') ? ['insurance'] : []}
        onClick={({ key }) => key !== 'logout' && handleMenuClick(key)}
      >
        <Menu.Item key="patients" icon={<UserOutlined />}>
          {t('patients')}
        </Menu.Item>
        {!isViewerOnly && <Menu.Item key="services" icon={<MedicineBoxOutlined />}>
          {t('internalServices')}
        </Menu.Item>}
        {!isViewerOnly && canSeeRevenueAnalytics && (
          <Menu.Item key="owner-revenue" icon={<FundOutlined />}>
            {t('ownerRevenue')}
          </Menu.Item>
        )}
        {!isViewerOnly && canManageOwnerFeatures && (
          <Menu.Item key="owner-doctors" icon={<PlusCircleOutlined />}>
            {t('ownerDoctors')}
          </Menu.Item>
        )}
        {!isViewerOnly && <Menu.Item key="inventory" icon={<InboxOutlined />}>
          {t('inventory')}
        </Menu.Item>}
        <Menu.Item key="external-services" icon={<ExperimentOutlined />}>
          {t('externalServices')}
        </Menu.Item>
        {!isViewerOnly && <Menu.Item key="referral-sources" icon={<TeamOutlined />}>
          {t('referralSources')}
        </Menu.Item>}
        {!isViewerOnly && <Menu.Item key="packages" icon={<GiftOutlined />}>
          {t('packages')}
        </Menu.Item>}
        {!isViewerOnly && <Menu.Item key="medicines" icon={<UnorderedListOutlined />}>
          {t('medicines')}
        </Menu.Item>}
        {!isViewerOnly && <Menu.Item key="attendance" icon={<ClockCircleOutlined />}>
          {t('attendanceTitle')}
        </Menu.Item>}
        {!isViewerOnly && <Menu.Item key="doctor-attendance" icon={<LoginOutlined />}>
          {t('doctorAttendanceTitle')}
        </Menu.Item>}
        {!isViewerOnly && <Menu.Item key="timetable" icon={<CalendarOutlined />}>
          الجدول والحجوزات
        </Menu.Item>}
        {!isViewerOnly && canSeeRevenueAnalytics && (
          <Menu.Item key="expenses" icon={<DollarCircleOutlined />}>
            {t('expenses')}
          </Menu.Item>
        )}
        {!isViewerOnly && canAccessInsurance && (
          <Menu.SubMenu key="insurance" icon={<SafetyCertificateOutlined />} title={t('insurance')}>
            <Menu.Item key="insurance-companies" icon={<SafetyCertificateOutlined />}>
              {t('insuranceCompanies')}
            </Menu.Item>
            <Menu.Item key="insurance-report" icon={<FileTextOutlined />}>
              {t('insuranceReports')}
            </Menu.Item>
          </Menu.SubMenu>
        )}
        {!isViewerOnly && <Menu.Item key="settings" icon={<SettingOutlined />}>
          {t('settings')}
        </Menu.Item>}
        {!isViewerOnly && canManageOwnerFeatures && (
          <Menu.Item key="owner-visit-reports" icon={<CalendarOutlined />}>
            {t('ownerVisitReports')}
          </Menu.Item>
        )}
        {!isViewerOnly && canManageOwnerFeatures && (
          <Menu.Item key="patient-export" icon={<FileExcelOutlined />}>
            {t('patientExportSidebar')}
          </Menu.Item>
        )}
        <Menu.Item key="logout" icon={<LogoutOutlined />} onClick={handleLogout}>
          {t('logout')}
        </Menu.Item>
      </Menu>
    </Sider>
  );
};

export default DashboardSidebar;
